import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { searchMentionSuggestions } from "../api/UserAPI";
import { activeMention, insertMention } from "../utils/mentions";

export const MENTION_DEBOUNCE_MS = 250;

const NO_RESULTS = { query: null, users: [] };

// @mention autocomplete for a controlled <input> or <textarea>. The text
// stays the caller's state; this tracks the caret and offers usernames for
// the "@partial" token at the caret (utils/mentions.activeMention).
// Suggestions are only a shortcut: a typed "@username" is resolved by the
// server the same way.
//
//   inputProps      spread on the field: onChange (calls onValueChange),
//                   caret tracking, focus and IME composition
//   handleKeyDown   call first in the field's onKeyDown; true when it
//                   consumed the key (arrows, Enter, Tab, Escape)
//   ariaProps       aria-controls / aria-activedescendant / aria-expanded
//   suggestions     props for <MentionSuggestions>, or null when closed
export function useMentionAutocomplete({ value, onValueChange, inputRef }) {
  const listboxId = useId();
  // The caret, recorded with the value it was read from: a value changed
  // from outside (submit clears it, an edit loads text) has no caret yet.
  const [selection, setSelection] = useState({ value: null, start: null, end: null });
  const [composing, setComposing] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(null);
  const [results, setResults] = useState(NO_RESULTS);
  const [highlight, setHighlight] = useState(0);
  const pendingCaretRef = useRef(null);

  const caret =
    selection.value === value && selection.start === selection.end ? selection.start : null;
  const active = useMemo(
    () => (composing || caret == null ? null : activeMention(value, caret)),
    [value, caret, composing]
  );
  const activeStart = active?.start ?? null;

  // Escape dismisses the token being typed; another token reopens.
  useEffect(() => {
    if (dismissedAt != null && activeStart !== dismissedAt) setDismissedAt(null);
  }, [activeStart, dismissedAt]);

  const query = active && active.start !== dismissedAt ? active.query : null;

  // Debounced; a newer query (or closing) aborts the request in flight.
  useEffect(() => {
    if (!query) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const users = await searchMentionSuggestions(query, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setResults({ query, users });
        setHighlight(0);
      } catch {
        if (!controller.signal.aborted) setResults({ query, users: [] });
      }
    }, MENTION_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Results for an earlier prefix of this query stay up (narrowed) until the
  // new ones arrive; results for anything else are never shown.
  const users = useMemo(() => {
    if (!query || results.query == null) return [];
    const q = query.toLowerCase();
    if (!q.startsWith(results.query.toLowerCase())) return [];
    return results.users.filter((u) => u.username.toLowerCase().startsWith(q));
  }, [query, results]);

  const open = focused && users.length > 0;
  const highlighted = open ? Math.min(highlight, users.length - 1) : -1;
  const optionId = (index) => `${listboxId}-option-${index}`;

  const syncSelection = useCallback((el) => {
    setSelection({ value: el.value, start: el.selectionStart, end: el.selectionEnd });
  }, []);

  const select = useCallback(
    (user) => {
      if (!active) return;
      const next = insertMention(value, active, user.username);
      pendingCaretRef.current = next.caret;
      setSelection({ value: next.text, start: next.caret, end: next.caret });
      setResults(NO_RESULTS);
      onValueChange(next.text);
    },
    [active, value, onValueChange]
  );

  // After an insertion: focus the field and put the caret after the mention.
  useLayoutEffect(() => {
    const caretAfter = pendingCaretRef.current;
    const el = inputRef.current;
    if (caretAfter == null || !el || el.value !== value) return;
    pendingCaretRef.current = null;
    el.focus();
    el.setSelectionRange(caretAfter, caretAfter);
  }, [value, inputRef]);

  const handleKeyDown = (e) => {
    // Keys during IME composition belong to the IME.
    if (!open || e.nativeEvent?.isComposing || e.keyCode === 229) return false;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlight((highlighted + 1) % users.length);
        return true;
      case "ArrowUp":
        e.preventDefault();
        setHighlight((highlighted - 1 + users.length) % users.length);
        return true;
      case "Enter":
      case "Tab":
        // Selecting never submits the form.
        e.preventDefault();
        select(users[highlighted]);
        return true;
      case "Escape":
        // Only the suggestions: not a reply/edit in progress or a modal.
        e.preventDefault();
        e.stopPropagation();
        setDismissedAt(active.start);
        return true;
      default:
        return false;
    }
  };

  const inputProps = {
    onChange: (e) => {
      syncSelection(e.target);
      onValueChange(e.target.value);
    },
    onSelect: (e) => syncSelection(e.target),
    onKeyUp: (e) => syncSelection(e.target),
    onClick: (e) => syncSelection(e.target),
    onFocus: (e) => {
      setFocused(true);
      syncSelection(e.target);
    },
    onBlur: () => setFocused(false),
    onCompositionStart: () => setComposing(true),
    onCompositionEnd: (e) => {
      setComposing(false);
      syncSelection(e.target);
    },
  };

  const ariaProps = {
    "aria-autocomplete": "list",
    "aria-expanded": open,
    "aria-controls": open ? listboxId : undefined,
    "aria-activedescendant": open ? optionId(highlighted) : undefined,
  };

  const suggestions = open
    ? {
        id: listboxId,
        optionId,
        anchorRef: inputRef,
        users,
        highlighted,
        onHighlight: setHighlight,
        onSelect: select,
      }
    : null;

  return { inputProps, handleKeyDown, ariaProps, suggestions };
}
